import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, CircleNotch } from "@phosphor-icons/react";
import { postLead } from "@/lib/api";

const HERO_IMG =
  "https://images.unsplash.com/photo-1760084836111-b384c6d32104?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const HEAD_LINES = ["Protecting What", "Matters Most —", "Coverage You Trust"];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.25 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export default function Hero() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", coverage_interest: "Both", consent: false,
  });
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error("Please accept the TCPA consent to continue.");
    setLoading(true);
    try {
      await postLead(form);
      toast.success("Request received — a licensed agent will call you shortly.");
      setForm({ name: "", email: "", phone: "", coverage_interest: "Both", consent: false });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="hero" className="relative grain bg-warm-white pt-[76px] overflow-hidden">
      {/* soft background accents */}
      <div className="pointer-events-none absolute -top-24 -right-24 w-[520px] h-[520px] rounded-full bg-navy/[0.04] blur-3xl" />
      <div className="pointer-events-none absolute top-1/3 -left-40 w-[420px] h-[420px] rounded-full bg-gold/[0.07] blur-3xl" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-8 pt-14 lg:pt-20 pb-24 grid lg:grid-cols-12 gap-12 lg:gap-8 items-start">
        {/* Left: kinetic headline */}
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
            className="eyebrow text-gold mb-6 flex items-center gap-3"
          >
            <span className="inline-block w-8 h-px bg-gold" />
            Licensed Life &amp; Health Agency · {`Indiana`}
          </motion.p>

          <h1 className="font-heading font-extrabold text-navy leading-[0.95] tracking-tighter text-[clamp(2.6rem,6.5vw,5.4rem)]">
            {HEAD_LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span
                  className="block"
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="visible"
                >
                  {i === 2 ? (
                    <>
                      Coverage <span className="text-gold">You Trust</span>
                    </>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-8 max-w-xl text-brand-slate text-lg leading-relaxed"
          >
            Independent guidance across Term, Whole &amp; Universal Life, Medicare, and
            family health plans — with a real, licensed agent who answers the phone.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.7 }}
            className="mt-10 relative max-w-lg"
          >
            <div className="clip-frame overflow-hidden rounded-xl border border-black/5">
              <img
                src={HERO_IMG}
                alt="A family enjoying time together, protected by the right coverage"
                className="w-full h-[220px] sm:h-[260px] object-cover"
                loading="eager"
              />
            </div>
            <div className="absolute -bottom-5 -right-3 sm:right-6 bg-navy text-warm-white rounded-xl px-5 py-4 shadow-xl shadow-navy/20">
              <p className="font-heading font-extrabold text-2xl leading-none">A+</p>
              <p className="text-[11px] text-light-slate mt-1 tracking-wide">Carrier ratings</p>
            </div>
          </motion.div>
        </div>

        {/* Right: lead form */}
        <motion.div
          initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5 lg:mt-4"
        >
          <div className="bg-navy rounded-2xl p-7 sm:p-8 relative overflow-hidden">
            <div className="pointer-events-none absolute -top-16 -right-16 w-52 h-52 rounded-full bg-gold/10 blur-2xl" />
            <p className="eyebrow text-gold">Free · No obligation</p>
            <h2 className="font-heading font-extrabold text-warm-white text-2xl sm:text-[27px] mt-2 leading-tight">
              Request your callback
            </h2>
            <p className="text-light-slate text-sm mt-2">
              Tell us a little about you. A licensed agent responds within one business day.
            </p>

            <form onSubmit={submit} data-testid="lead-form" className="mt-6 space-y-3.5">
              <Field label="Full name" testid="lead-name" value={form.name}
                onChange={(v) => set("name", v)} placeholder="Jane Doe" required />
              <Field label="Email" type="email" testid="lead-email" value={form.email}
                onChange={(v) => set("email", v)} placeholder="jane@email.com" required />
              <Field label="Phone" type="tel" testid="lead-phone" value={form.phone}
                onChange={(v) => set("phone", v)} placeholder="(555) 123-4567" required />

              <div>
                <label className="block text-[11px] uppercase tracking-[0.18em] text-light-slate mb-2">
                  Coverage interest
                </label>
                <div className="grid grid-cols-3 gap-2" data-testid="lead-coverage">
                  {["Life", "Health", "Both"].map((opt) => (
                    <button
                      type="button"
                      key={opt}
                      data-testid={`coverage-${opt.toLowerCase()}`}
                      onClick={() => set("coverage_interest", opt)}
                      className={`rounded-lg py-2.5 text-sm font-semibold transition-all ${
                        form.coverage_interest === opt
                          ? "bg-gold text-navy"
                          : "bg-white/5 text-light-slate hover:bg-white/10"
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              <label className="flex items-start gap-3 pt-1 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="lead-consent"
                  checked={form.consent}
                  onChange={(e) => set("consent", e.target.checked)}
                  className="mt-1 h-4 w-4 shrink-0 accent-gold"
                />
                <span className="text-[11px] leading-relaxed text-light-slate">
                  By clicking submit, you authorize a licensed agent to contact you via phone or
                  email regarding insurance options.
                </span>
              </label>

              <button
                type="submit"
                data-testid="lead-submit"
                disabled={loading}
                className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-warm-white px-6 py-3.5 text-navy font-semibold transition-all hover:bg-gold disabled:opacity-60 active:scale-[0.98]"
              >
                {loading ? <CircleNotch className="animate-spin" size={20} /> : "Request Callback"}
                {!loading && <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />}
              </button>
            </form>
          </div>
          <button
            onClick={() => scrollTo("calculator")}
            className="mt-4 w-full text-center text-sm text-brand-slate hover:text-navy transition-colors"
          >
            Not sure what you need? Try the coverage calculator ↓
          </button>
        </motion.div>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", testid, required }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.18em] text-light-slate mb-1.5">
        {label}
      </label>
      <input
        type={type}
        data-testid={testid}
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-warm-white placeholder:text-light-slate/50 text-sm outline-none transition-all focus:border-gold focus:bg-white/[0.08]"
      />
    </div>
  );
}
